import React, { useEffect, useState, useMemo } from "react";
import { apiClient } from "../../utils/apiClient";
import videoPause from "../../../public/assets/video-pause.svg";
import videoPlay from "../../../public/assets/video-play.svg";
import { renderBlocksHero } from "../../utils/richtext";
import GeneralAnchor from "../general-anchor/general-anchor.component";

const getMediaUrl = (url) => {
  if(true) {
    return url;
  }
  return `https://dev.intermediaries.hsbc.co.uk${url}`;
}

const isImageUrl = (url) => {
  if (!url) return false;
  return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url);
};

const MediaElement = ({ isMediaImage, mediaObjectUrl, mediaAltText, mediaLinkUrl }) => {
  const videoRef = React.useRef(null);
  const [isHovering, setIsHovering] = React.useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = React.useState(false);

  const togglePlayback = (e) => {
    e.stopPropagation();
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch((error) => {
        console.error('Error attempting to play video:', error);
      });
    } else {
      el.pause();
    }
  };

  if (isMediaImage) {
    const imageElement = <img src={mediaObjectUrl} alt={mediaAltText || ''} />;
    if (mediaLinkUrl) {
      return (
        <a
          href={mediaLinkUrl}
          {...(mediaLinkUrl.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
          {imageElement}
        </a>
      );
    }
    return imageElement;
  }

  return (
    <div
      className={'hero-banner-dynamic__video-container'}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}>
      <video
        ref={videoRef}
        src={mediaObjectUrl}
        autoPlay
        muted
        loop
        onPlay={() => setIsVideoPlaying(true)}
        onPause={() => setIsVideoPlaying(false)} />
      {isHovering && (
        <div className="hero-banner-dynamic__video-controls" onClick={togglePlayback}>
          <button className={`hero-banner-dynamic__play-pause-button ${isVideoPlaying ? 'playing' : ''}`}>
            <img src={isVideoPlaying ? videoPause : videoPlay} alt={isVideoPlaying ? 'Pause' : 'Play'} />
          </button>
        </div>
      )}
    </div>
  );
};

const HeroBannerDynamic = () => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  const [item, setItem] = useState({});
  const [objectUrl, setObjectUrl] = useState(null);
  const [mediaObjectUrl, setMediaObjectUrl] = useState(null);
  const [isMediaImage, setIsMediaImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const DEKSTOP_WIDTH = 1025;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('banners/current?type=Home');

        if (response) {
          const {
            textBoxColor,
            content,
            ctaButtonLabel,
            ctaButtonUrl,
            background,
            backgroundAltText,
            media,
            mediaAltText,
            mediaPosition,
            imageTextRatio,
            overlayPadding,
            bannerPadding,
            mediaLinkUrl,
          } = response;

          if (background?.url) {
            setObjectUrl(getMediaUrl(background.url));
          }

          if (media?.url) {
            setMediaObjectUrl(getMediaUrl(media.url));
            setIsMediaImage(isImageUrl(media.url));
          }

          setItem({
            textBoxColor,
            content: JSON.parse(content)?.content,
            ctaButtonLabel,
            ctaButtonUrl,
            mediaAltText,
            mediaPosition,
            imageTextRatio,
            overlayPadding,
            bannerPadding,
            media,
            mediaLinkUrl,
            backgroundAltText,
          });
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to fetch banner data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const outerStyle = useMemo(() => {
    let mediaBasis = '0';
    let contentBasis = '100';
    let gap = '0';

    if (item.media) {
      const [val1, val2] = item.imageTextRatio?.split('-') || ['50', '50'];
      mediaBasis = item.mediaPosition === 'right' ? val2 : val1;
      contentBasis = item.mediaPosition === 'right' ? val1 : val2;
      gap = windowWidth >= DEKSTOP_WIDTH ? '40' : '16';
    }

    const style = {
      '--media-basis': `${mediaBasis}%`,
      '--content-basis': `${contentBasis}%`,
      '--gap': `${gap}px`,
    };

    if (windowWidth >= DEKSTOP_WIDTH) {
      style.padding = item.bannerPadding;
    }

    return style;
  }, [item.imageTextRatio, item.bannerPadding, item.mediaPosition, windowWidth, item.media]);

  if (loading) {
    return <>Loading...</>;
  }
  return (
    <div className={'hero-banner-dynamic'}>
      <img className={'hero-banner-dynamic__background-image'} src={objectUrl || undefined} alt={item.backgroundAltText || ''} />
      <div className={'hero-banner-dynamic__outer'} style={outerStyle} data-media-position={item.mediaPosition}>
        {item.media && mediaObjectUrl && (
          <div className={'hero-banner-dynamic__media'}>
            <MediaElement
              isMediaImage={isMediaImage}
              mediaObjectUrl={mediaObjectUrl}
              mediaAltText={item.mediaAltText}
              mediaLinkUrl={item.mediaLinkUrl}
            />
          </div>
        )}
        <section
          className={'hero-banner-dynamic__content'}
          style={{
            backgroundColor: item.textBoxColor,
            ...(windowWidth >= DEKSTOP_WIDTH && { padding: item.overlayPadding }),
          }}>
          {renderBlocksHero((item.content?.blocks || []))}
          {item.ctaButtonLabel && item.ctaButtonUrl && (
            <div className={'hero-banner-dynamic__button-container'}>
              <GeneralAnchor additionalClass="hero-banner-dynamic__button" isRed href={item.ctaButtonUrl}>
                {item.ctaButtonLabel}
              </GeneralAnchor>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default HeroBannerDynamic;
